import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';

const DEFAULT_SIZE = 'small';
const ARIA_POLITENESS_ASSERTIVE = 'assertive';
/** required to be able to read same message multiple times https://github.com/angular/components/blob/7beb95219ddc9a16cd28ee2524bf1594e04c1c58/src/cdk/a11y/live-announcer/live-announcer.ts#L112-L127 */
const ANNOUNCER_DURATION = 1000;
class NxSpinnerComponent {
    /** Sets the size of the spinner. Default is 'small'. */
    set size(value) {
        if (value !== this._size) {
            this._size = value;
            this._cdr.markForCheck();
        }
    }
    get size() {
        return this._size;
    }
    /** Whether the spinner should use a negative styling. */
    set negative(value) {
        if (value !== this._negative) {
            this._negative = coerceBooleanProperty(value);
            this._cdr.markForCheck();
        }
    }
    get negative() {
        return this._negative;
    }
    /**
     * Message that will be announced by screen readers at instantiation.
     * Do __not__ combine with an `aria-live` area.
     * Empty messages will be ignored. Defaults to empty/no message
     */
    set activationAnnouncement(value) {
        this._activationAnnouncement = value;
    }
    get activationAnnouncement() {
        return this._activationAnnouncement;
    }
    /**
     * Message that will be announced by screen readers at destroy.
     * Do __not__ combine with an `aria-live` area.
     * Empty messages will be ignored. Defaults to empty/no message
     */
    set completionAnnouncement(value) {
        this._completionAnnouncement = value;
    }
    get completionAnnouncement() {
        return this._completionAnnouncement;
    }
    constructor(_cdr, liveAnnouncer) {
        this._cdr = _cdr;
        this.liveAnnouncer = liveAnnouncer;
        this._size = DEFAULT_SIZE;
        this._negative = false;
        this._activationAnnouncement = '';
        this._completionAnnouncement = '';
    }
    ngOnInit() {
        this.announce(this.activationAnnouncement);
    }
    ngOnDestroy() {
        this.announce(this.completionAnnouncement);
    }
    async announce(message) {
        if (message.length > 0) {
            await this.liveAnnouncer.announce(message, ARIA_POLITENESS_ASSERTIVE, ANNOUNCER_DURATION);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxSpinnerComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.2", type: NxSpinnerComponent, selector: "nx-spinner", inputs: { size: "size", negative: "negative", activationAnnouncement: "activationAnnouncement", completionAnnouncement: "completionAnnouncement" }, host: { properties: { "class.nx-spinner--small": "size === \"small\"", "class.nx-spinner--medium": "size === \"medium\"", "class.nx-spinner--large": "size === \"large\"", "class.nx-spinner--negative": "negative", "attr.role": "\"status\"", "attr.aria-live": "\"assertive\"", "attr.aria-hidden": "\"true\"" } }, ngImport: i0, template: "<div class=\"nx-spinner__spin-block\">\n    <div class=\"nx-spinner__edge-left\"></div>\n    <div class=\"nx-spinner__edge-right\"></div>\n</div>\n", styles: ["@keyframes spin{0%{transform:rotate(45deg)}to{transform:rotate(405deg)}}:host{position:relative;display:block}:host(.nx-spinner--small){width:16px;height:16px}:host(.nx-spinner--medium){width:24px;height:24px}:host(.nx-spinner--large){width:40px;height:40px}.nx-spinner__spin-block{width:100%;height:100%;transform:rotate(45deg);border-style:solid;border-radius:50%;border-color:var(--spinner-indicator-color) var(--spinner-background-color) var(--spinner-background-color) var(--spinner-background-color);animation-name:spin;animation-duration:.8s;animation-iteration-count:infinite;animation-timing-function:linear}:host(.nx-spinner--small) .nx-spinner__spin-block{border-width:1.6px}:host(.nx-spinner--medium) .nx-spinner__spin-block{border-width:2.4px}:host(.nx-spinner--large) .nx-spinner__spin-block{border-width:4px}.nx-spinner__edge-left,.nx-spinner__edge-right{width:12%;height:12%;border-radius:50%;background-color:var(--spinner-indicator-color);position:absolute;top:4%}:host(.nx-spinner--small) .nx-spinner__edge-left,:host(.nx-spinner--small) .nx-spinner__edge-right{width:10%;height:10%}.nx-spinner__edge-left{left:4%}.nx-spinner__edge-right{right:4%}:host(.nx-spinner--negative) .nx-spinner__spin-block{border-color:var(--spinner-negative-indicator-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color)}:host(.nx-spinner--negative) .nx-spinner__edge-left,:host(.nx-spinner--negative) .nx-spinner__edge-right{background-color:var(--spinner-negative-indicator-color)}@media screen and (forced-colors: active){:host .nx-spinner__spin-block,:host.nx-spinner--negative .nx-spinner__spin-block{border-color:Canvas highlight highlight highlight}}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxSpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'nx-spinner', changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        '[class.nx-spinner--small]': 'size === "small"',
                        '[class.nx-spinner--medium]': 'size === "medium"',
                        '[class.nx-spinner--large]': 'size === "large"',
                        '[class.nx-spinner--negative]': 'negative',
                        '[attr.role]': '"status"',
                        '[attr.aria-live]': '"assertive"',
                        '[attr.aria-hidden]': '"true"',
                    }, template: "<div class=\"nx-spinner__spin-block\">\n    <div class=\"nx-spinner__edge-left\"></div>\n    <div class=\"nx-spinner__edge-right\"></div>\n</div>\n", styles: ["@keyframes spin{0%{transform:rotate(45deg)}to{transform:rotate(405deg)}}:host{position:relative;display:block}:host(.nx-spinner--small){width:16px;height:16px}:host(.nx-spinner--medium){width:24px;height:24px}:host(.nx-spinner--large){width:40px;height:40px}.nx-spinner__spin-block{width:100%;height:100%;transform:rotate(45deg);border-style:solid;border-radius:50%;border-color:var(--spinner-indicator-color) var(--spinner-background-color) var(--spinner-background-color) var(--spinner-background-color);animation-name:spin;animation-duration:.8s;animation-iteration-count:infinite;animation-timing-function:linear}:host(.nx-spinner--small) .nx-spinner__spin-block{border-width:1.6px}:host(.nx-spinner--medium) .nx-spinner__spin-block{border-width:2.4px}:host(.nx-spinner--large) .nx-spinner__spin-block{border-width:4px}.nx-spinner__edge-left,.nx-spinner__edge-right{width:12%;height:12%;border-radius:50%;background-color:var(--spinner-indicator-color);position:absolute;top:4%}:host(.nx-spinner--small) .nx-spinner__edge-left,:host(.nx-spinner--small) .nx-spinner__edge-right{width:10%;height:10%}.nx-spinner__edge-left{left:4%}.nx-spinner__edge-right{right:4%}:host(.nx-spinner--negative) .nx-spinner__spin-block{border-color:var(--spinner-negative-indicator-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color) var(--spinner-negative-background-color)}:host(.nx-spinner--negative) .nx-spinner__edge-left,:host(.nx-spinner--negative) .nx-spinner__edge-right{background-color:var(--spinner-negative-indicator-color)}@media screen and (forced-colors: active){:host .nx-spinner__spin-block,:host.nx-spinner--negative .nx-spinner__spin-block{border-color:Canvas highlight highlight highlight}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i1.LiveAnnouncer }], propDecorators: { size: [{
                type: Input
            }], negative: [{
                type: Input
            }], activationAnnouncement: [{
                type: Input
            }], completionAnnouncement: [{
                type: Input
            }] } });

class NxSpinnerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.2", ngImport: i0, type: NxSpinnerModule, declarations: [NxSpinnerComponent], imports: [CommonModule], exports: [NxSpinnerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxSpinnerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.2", ngImport: i0, type: NxSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [NxSpinnerComponent],
                    imports: [CommonModule],
                    exports: [NxSpinnerComponent],
                    bootstrap: [],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { NxSpinnerComponent, NxSpinnerModule };
